import { createSlice } from '@reduxjs/toolkit';
import {
  getActiveTicketsData,
  getCompletedTicketsData,
  getTicketMessagesData,
  getTicketMessagesFromOrdersData,
  getTicketDataData,
  postSendTicketMessage,
  postEditTicketData,
  postToggleTicketsComplete,
  getTicketPageData,
  postSetTicketMessagesRead,
  postSetTicketsMessagesRead,
  postTicketBoomerang,
  postTicketAutoClosure
} from '../../api/tickets';
import { getUnreadData } from './dashboard';
import { ticketMessageSubmitAllPictures } from '../../components/dashboard/tickets/ticket/newTicket/ticket-message-input-cleanup';
import { markMetricRemoved, markMetricAdded, NotificationMetricType } from './notifications';

// ----------------------------------------------------------------------
const initialState = {
  isLoading: false,
  ticketIsLoading: false,
  error: null,
  isAddingNewTicket: false,
  data: {
    activeTickets: [],
    completedTickets: [],
    ticketById: {},
    ticketInfoById: {},
    facilities: [],
    users: [],
    unreadCount: 0,
    ticketTypes: [],
  },
};

const slice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    startTicketLoading(state) {
      state.isLoading = true;
      state.ticketIsLoading = true;
    },
    clearItemData(state) {
      state.data.ticketInfoById = {};
      state.data.ticketById = {};
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getActiveTicketsSuccess(state, action) {
      state.isLoading = false;
      state.data.activeTickets = action.payload;
    },

    getCompletedTicketsSuccess(state, action) {
      state.isLoading = false;
      state.data.completedTickets = action.payload;
    },

    getTicketMessagesSuccess(state, action) {
      state.isLoading = false;
      state.data.ticketById[action.payload.id] = action.payload;
      state.ticketIsLoading = false;
    },

    getTicketDataSuccess(state, action) {
      state.isLoading = false;
      state.data.ticketInfoById[action.payload.id] = action.payload;
    },

    sendTicketMessageSuccess(state, action) {
      state.isLoading = false;
      const { ticketId, messages } = action.payload;
      if (state.data.ticketById[ticketId].ticketMessages) state.data.ticketById[ticketId].ticketMessages.push(...messages);
      else state.data.ticketById[ticketId].ticketMessages = [...messages];
    },

    updateSingleTicket(state, action) {
      const urlParams = new URLSearchParams(window.location.search);
      const filter = urlParams.get('status');
      const updatedTicket = action.payload;
      const { activeTickets, completedTickets } = state.data;
      if (filter === 'active' || !filter) {
        const updatedActiveTickets = activeTickets.map((t) => t.id === parseInt(updatedTicket.id, 10) ? { ...t, ...updatedTicket } : t);
        state.data.activeTickets = updatedActiveTickets;
      } else if (filter === 'completed') {
        const updatedCompletedTickets = completedTickets.map((t) => t.id === parseInt(updatedTicket.id, 10) ? { ...t, ...updatedTicket } : t);
        state.data.completedTickets = updatedCompletedTickets;
      }
      state.isLoading = false;
    },

    updateTicketsComplete(state, action) {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const { activeTickets, completedTickets } = state.data;
      const updatedTicketArray = action.payload;
      const updatedTicketIds = updatedTicketArray.map((t) => t.id);
      if (status !== 'complete') {
        const updatedActiveTickets = activeTickets.filter((t) => !updatedTicketIds.includes(t.id));
        state.data.activeTickets = updatedActiveTickets;
      } else {
        const updatedCompletedTickets = completedTickets.filter((t) => !updatedTicketIds.includes(t.id));
        state.data.completedTickets = updatedCompletedTickets;
      }
      state.isLoading = false;
    },

    updateTicketInfo(state, action) {
      const updatedTicketInfo = action.payload;
      if (updatedTicketInfo.parentTicketId > 0) {
        const updatedSubTickets = state.data.ticketInfoById[updatedTicketInfo.parentTicketId].subtickets.map((t) =>
          t.id === updatedTicketInfo.id ? { ...updatedTicketInfo } : t
        );
        state.data.ticketInfoById[updatedTicketInfo.parentTicketId].subtickets = updatedSubTickets;
      } else {
        state.data.ticketInfoById[updatedTicketInfo.id].mainTicket = updatedTicketInfo;
      }
    },

    markTicketAsReadInRedux(state, action) {
      const ticketId = action.payload;
      const { activeTickets } = state.data;

      const updatedTicketMessages = activeTickets.map((ticket) => {
        if (ticket.id === parseInt(ticketId, 10)) {
          ticket.messageCount = 0;
          return ticket;
        }
        return ticket;
      });
      state.data.activeTickets = updatedTicketMessages;
    },

    updateListFromObj(state, action) {
      const objList = action.payload;
      Object.keys(objList).forEach((key) => {
        state.data[key] = objList[key];
      });
      state.isLoading = false;
      state.data = { ...state.data, ...objList };
    },

    getUnreadTicketCountSuccess(state, action) {
      const { unreadCount } = action.payload;
      state.isLoading = false;
      state.unreadCount = unreadCount;
    },
    setBoomerangDate(state, action) {
      state.data.ticketInfoById[action.payload.id].boomerang = action.payload.data;
    },
    setAutoClosure(state, action) {
      state.data.ticketInfoById[action.payload.id].autoClosure = action.payload.data;
    },
    setIsAddingNewTicket(state, action) {
      state.isAddingNewTicket = action.payload
    }
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { updateSingleTicket, updateTicketInfo, setIsAddingNewTicket } = slice.actions;

export function getActiveTickets(clearItemData = true) {
  return async (dispatch) => {
    if(clearItemData) dispatch(slice.actions.clearItemData());
    dispatch(slice.actions.startLoading());
    try {
      const response = await getActiveTicketsData();
      const sortedActiveTickets = response.data.sort(
        (a, b) => new Date(a.lastSentOn) < new Date(b.lastSentOn) ? 1 : -1
      )
      dispatch(slice.actions.getActiveTicketsSuccess(sortedActiveTickets));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCompletedTickets({ startDate, endDate }) {
  startDate = new Date(startDate).toISOString().split('T')[0];
  endDate = new Date(endDate).toISOString().split('T')[0];
  return async (dispatch) => {
    dispatch(slice.actions.clearItemData());
    dispatch(slice.actions.startLoading());
    try {
      const response = await getCompletedTicketsData({ startDate, endDate });
      dispatch(slice.actions.getCompletedTicketsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTicketMessages({ id }) {
  return async (dispatch) => {
    dispatch(slice.actions.startTicketLoading());
    try {
      const response = await getTicketMessagesData(id);
      dispatch(slice.actions.getTicketMessagesSuccess({ ...response.data, id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getTicketMessagesFromOrder(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getTicketMessagesFromOrdersData(id);
      dispatch(slice.actions.getTicketMessagesSuccess({ ...response.data, id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getTicketData(id, noSubticket = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getTicketDataData({ id });
      if (noSubticket !== null && response.data?.mainTicket?.parentTicketId > 0) noSubticket(response.data.mainTicket.parentTicketId);
      dispatch(slice.actions.getTicketDataSuccess({ ...response.data, id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function sendTicketMessage({
  message,
  ticketNumber,
  notify = false,
  files,
  users,
  sentBy,
  fullName
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    message = await ticketMessageSubmitAllPictures(message);
    const placeHolderMsgs = [];

    if (message && message !== '' && message !== '<p><br></p>') {
      placeHolderMsgs.push({
        id: 0,
        ticketNumber,
        note: message,
        file: false,
        attachFileId: 0,
        sentBy,
        fullName,
        sentOn: new Date().toISOString(),
        systemMessage: false,
        deleteFlag: false,
        emailMessage: false,
      });
    }

    if (files?.length) {
      placeHolderMsgs.push({
        id: 1,
        ticketNumber,
        note: 'Files Loading...',
        file: false,
        attachFileId: 0,
        sentBy,
        fullName,
        sentOn: new Date().toISOString(),
        systemMessage: false,
        deleteFlag: false,
        emailMessage: false,
      });
    }

    dispatch(
      slice.actions.sendTicketMessageSuccess({
        ticketId: ticketNumber,
        messages: placeHolderMsgs,
      })
    );

    try {
      const response = await postSendTicketMessage({ message, ticketNumber, notify, files, users });
      dispatch(slice.actions.getTicketMessagesSuccess({ ...response.data, id: ticketNumber }));
      return true;
    } catch (error) {
      console.error({ error });
      dispatch(slice.actions.hasError(error));
    }
    return false;
  };
}

export function editTicketData({ Id, value, updateType, mainId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postEditTicketData({ Id, value, updateType });
      if (!response.data) return false;
      if (updateType === 'Complete' || updateType === 'Users') {
        dispatch(getTicketMessages({ id: Id }));
      }
      if (mainId) await dispatch(getTicketData(mainId));
      else await dispatch(getTicketData(Id));
    } catch (error) {
      console.error({ error });
      dispatch(slice.actions.hasError(error));
      return false;
    }
    return true;
  };
}

export function toggleTicketsComplete({ ticketIds, isComplete }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postToggleTicketsComplete({ ticketIds, isComplete });
      if (response.data) {
        dispatch(slice.actions.updateTicketsComplete(response.data));
        if (isComplete) {
          dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ticketIds));
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getFacilitiesAndUsersList(callback = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getTicketPageData();
      dispatch(slice.actions.updateListFromObj(response.data));
      if (callback) callback(response.data);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setUnreadTicketCount(ticketCount) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getUnreadTicketCountSuccess({ unreadCount: ticketCount }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markAsRead(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.markTicketAsReadInRedux(id));
      const response = await postSetTicketMessagesRead({ id, markAsUnread: false, setSubticketsRead: false });
      if (response.status === 200) {
        dispatch(getUnreadData());
        dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, id));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markAllAsReadOrUnread(ids, markAsUnread) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postSetTicketsMessagesRead(ids, markAsUnread);
      if (response.status === 200) {
        dispatch(getActiveTickets());
        if (markAsUnread) {
          dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, ids));
        } else {
          dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ids));
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setBoomerangDate(date, ticketId, enqueueSnackbar) {
  return async (dispatch) => {
    try {
      const response = await postTicketBoomerang({ date, ticketId });
      if (response.status === 200 || (response.status === 204 && !date)) {
        dispatch(slice.actions.setBoomerangDate({ data: response.data, id: ticketId }));
        if (enqueueSnackbar) enqueueSnackbar('Reminder set successfully', { variant: 'success' });
      } else if (enqueueSnackbar) { enqueueSnackbar('Failed to set reminder', { variant: 'error' }); }
    } catch (error) {
      enqueueSnackbar('Failed to set reminder', { variant: 'error' });
    }
  };
}

 export function setAutoClosure(autoCloseOn, autoClosureId, ticketId, enqueueSnackbar) {
    return async (dispatch) => {
      try {
        const response = await postTicketAutoClosure({ autoCloseOn, autoClosureId, ticketId });
        if (response.status === 200 || (response.status === 204 && !autoCloseOn)) {
          dispatch(slice.actions.setAutoClosure({ data: response.data, id: ticketId }));
          if (autoCloseOn) {
            dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ticketId));
          } else {
            dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, ticketId));
          }
          if (enqueueSnackbar) {
            enqueueSnackbar(autoCloseOn ? 'Auto complete set successfully' : 'Auto complete canceled successfully', { variant: 'success' });
          }
        }
        else if (enqueueSnackbar) {
          enqueueSnackbar(autoCloseOn ? 'Failed to set auto complete' : 'Failed to cancel auto complete', { variant: 'error' });
        }
      }
      catch (error) {
        if (enqueueSnackbar) {
          enqueueSnackbar(autoCloseOn ? 'Failed to set auto complete' : 'Failed to cancel auto complete', { variant: 'error' });
        }
      }
    }
  };
